import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { api } from '../../services/api';
import { Toast } from '../../components/base/Toast';

interface HabitType {
  id: string;
  nom: string;
  prix: number | string;
  description?: string;
}

export default function ParametresHabitTypes() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [types, setTypes] = useState<HabitType[]>([]);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [toast, setToast] = useState<{ message: string, type: 'success' | 'error' } | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newType, setNewType] = useState({ nom: '', prix: '' });
  const [editingId, setEditingId] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem('atelier_bassoum_user');
    const token = localStorage.getItem('atelier_bassoum_token');
    if (stored && token) {
      setUser(JSON.parse(stored));
      fetchTypes(token);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const fetchTypes = async (token: string) => {
    try {
      const data = await api.getHabitTypes(token);
      if (Array.isArray(data)) {
        setTypes(data.map(t => ({ ...t, id: String(t.id) })));
      }
    } catch (err) {
      console.error(err);
      showToast("Erreur lors de la récupération des types", "error");
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleAdd = async () => {
    if (!newType.nom.trim()) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      await api.saveHabitType(token, {
          nom: newType.nom,
          prix: parseFloat(newType.prix) || 0
      });
      showToast("Type de couture ajouté");
      setNewType({ nom: '', prix: '' });
      setShowAddForm(false);
      fetchTypes(token);
    } catch (err) {
      showToast("Erreur lors de l'ajout", "error");
    }
  };

  const handleUpdate = async (type: HabitType) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      await api.saveHabitType(token, {
          id: parseInt(type.id),
          nom: type.nom,
          prix: parseFloat(String(type.prix)) || 0
      });
      showToast("Type mis à jour");
      setEditingId(null);
      fetchTypes(token);
    } catch (err) {
      showToast("Erreur lors de la mise à jour", "error");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Supprimer ce type de couture ?")) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      await api.deleteHabitType(token, parseInt(id));
      showToast("Type supprimé");
      fetchTypes(token);
    } catch (err) {
      showToast("Erreur lors de la suppression", "error");
    }
  };

  if (!user) return null;

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="hidden md:block">
        <Sidebar role={user?.role || 'gerant'} nom={user?.nom || 'Utilisateur'} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
             <button onClick={() => navigate(-1)} title="Retour" className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors">
               <i className="ri-arrow-left-s-line text-gray-600" />
             </button>
             <div>
               <h1 className="font-playfair font-bold text-lg text-gray-900">Types de couture</h1>
               <p className="text-xs text-gray-400 mt-0.5">{types.length} éléments configurés</p>
             </div>
          </div>
          <button 
            onClick={() => setShowAddForm(true)}
            className="bg-foret-500 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-foret-600 transition-colors shadow-sm cursor-pointer"
          >
            + Nouveau type
          </button>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-2xl mx-auto space-y-4">
            {showAddForm && (
              <div className="bg-white p-6 rounded-2xl border border-foret-100 shadow-sm animate-in fade-in slide-in-from-top-2">
                <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Ajouter un type de couture</h3>
                <div className="grid grid-cols-2 gap-4">
                   <div className="col-span-2 sm:col-span-1">
                      <label className="block text-[10px] font-bold text-gray-400 mb-1 uppercase tracking-widest">Désignation</label>
                      <input 
                        type="text" 
                        value={newType.nom} 
                        onChange={e => setNewType({...newType, nom: e.target.value})}
                        className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-foret-500 text-sm font-bold"
                        placeholder="ex: Boubou 3 pièces"
                      />
                   </div>
                   <div className="col-span-2 sm:col-span-1">
                      <label className="block text-[10px] font-bold text-gray-400 mb-1 uppercase tracking-widest">Prix de base (FCFA)</label>
                      <input 
                        type="number" 
                        value={newType.prix} 
                        onChange={e => setNewType({...newType, prix: e.target.value})}
                        className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-foret-500 text-sm font-bold"
                        placeholder="0"
                      />
                   </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                   <button onClick={() => setShowAddForm(false)} className="px-4 py-2 text-xs font-bold text-gray-400 hover:text-gray-600 cursor-pointer">Annuler</button>
                   <button onClick={handleAdd} className="px-6 py-2 bg-foret-500 text-white text-xs font-bold rounded-xl hover:bg-foret-600 transition-colors cursor-pointer">Enregistrer</button>
                </div>
              </div>
            )}

            <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
                <div className="divide-y divide-gray-50">
                   {loading ? (
                     <div className="p-10 text-center"><i className="ri-loader-4-line animate-spin text-2xl text-foret-500" /></div>
                   ) : types.length === 0 ? (
                     <div className="p-10 text-center text-gray-400 text-sm">Aucun type de couture configuré.</div>
                   ) : types.map(t => (
                     <div key={t.id} className="p-5 flex items-center justify-between group hover:bg-gray-50/50 transition-colors">
                        {editingId === t.id ? (
                          <div className="flex-1 flex flex-col sm:flex-row gap-3">
                             <input 
                                autoFocus
                                type="text" 
                                value={t.nom} 
                                onChange={e => setTypes(types.map(it => it.id === t.id ? {...it, nom: e.target.value} : it))}
                                className="flex-1 px-3 py-1.5 bg-gray-100 border-none rounded-lg text-sm font-bold"
                                aria-label="Nom du type de couture"
                                placeholder="Désignation"
                             />
                             <input 
                                type="number" 
                                value={t.prix} 
                                onChange={e => setTypes(types.map(it => it.id === t.id ? {...it, prix: e.target.value} : it))}
                                className="w-32 px-3 py-1.5 bg-gray-100 border-none rounded-lg text-sm font-bold"
                                aria-label="Prix du type de couture"
                                placeholder="Prix"
                             />
                             <div className="flex gap-2">
                               <button onClick={() => handleUpdate(t)} className="px-3 py-1.5 bg-foret-500 text-white rounded-lg text-xs font-bold cursor-pointer">OK</button>
                               <button onClick={() => setEditingId(null)} className="px-3 py-1.5 bg-gray-200 text-gray-600 rounded-lg text-xs font-bold cursor-pointer">✕</button>
                             </div>
                          </div>
                        ) : (
                          <>
                             <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-foret-50 rounded-xl flex items-center justify-center text-foret-500">
                                   <i className="ri-scissors-2-line" />
                                </div>
                                <div>
                                   <p className="text-sm font-bold text-gray-900">{t.nom}</p>
                                   <div className="flex flex-col items-start gap-1">
                                      <p className="text-[10px] font-black text-foret-500 uppercase tracking-widest">
                                        {(Number(t.prix) || 0).toLocaleString()} FCFA
                                      </p>
                                      {t.description && <p className="text-[9px] text-gray-400 italic line-clamp-1">{t.description}</p>}
                                   </div>
                                </div>
                             </div>
                             <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => setEditingId(t.id)} title="Modifier" aria-label="Modifier ce type" className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-400 hover:bg-foret-100 hover:text-foret-600 cursor-pointer transition-colors">
                                   <i className="ri-edit-line" />
                                </button>
                                <button onClick={() => handleDelete(t.id)} title="Supprimer" aria-label="Supprimer ce type" className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-400 hover:bg-red-100 hover:text-red-500 cursor-pointer transition-colors">
                                   <i className="ri-delete-bin-line" />
                                </button>
                             </div>
                          </>
                        )}
                     </div>
                   ))}
                </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
